import { FC } from 'react';
import { isNil, toString } from 'lodash';
import { MAX_CHAR_LENGTH } from 'common/constants/general';
import { largeCurrencyFormat, twoDecimalPercentFormat } from 'common/formats/formats';
import { Cells, DataEditorProps, GridDateProps, ValueViewerProps } from 'common/types/scalarSpreadsheet';
import { GridDate, GridDateEditor, GridDateValueViewer } from 'components/FeaturedSpreadsheet/components';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  FUND_INVESTMENTS_SUMMARY_SPREADSHEET_ALLOW_NEGATIVE_VALUE,
  FUND_INVESTMENTS_SUMMARY_SPREADSHEET_COLUMNS,
  FUND_INVESTMENTS_SUMMARY_SPREADSHEET_EXCLUDED_ROWS_COUNT,
  FUND_INVESTMENTS_SUMMARY_SPREADSHEET_INVESTMENT_VEHICLE_ALIAS,
  FUND_INVESTMENTS_SUMMARY_SPREADSHEET_MARKET_ADJUSTMENT_CLASSNAMES,
  FUND_INVESTMENTS_SUMMARY_SPREADSHEET_ROW_SPAN,
  FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TITLE_ALIAS,
  FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TOTAL_ALIAS,
  getFundInvestmentSummaryColumn,
} from 'pages/FundInvestments/common/constants';
import { FundInvestmentSummaryCellData } from 'pages/FundInvestments/hooks/useFundInvestmentSummarySheet';
import { generateAllRowsExpression, getExpression, getObjectValue, shortDate } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { CellParserParams, CreateParserParams } from './createParser.types';
import { normalizeFundCell } from './utils';

const {
  capital_account_date: CAPITAL_ACCOUNT_DATE,
  committed_capital: COMMITTED_CAPITAL,
  invested_capital: INVESTED_CAPITAL,
  realized_income: REALIZED_INCOME,
  unrealized_income: UNREALIZED_INCOME,
  distributions: DISTRIBUTIONS,
  net_capital: NET_CAPITAL,
  market_adjustment: MARKET_ADJUSTMENT,
  concluded_fair_value_of_net_capital: CONCLUDED_FAIR_VALUE_OF_NET_CAPITAL,
} = FUND_INVESTMENTS_SUMMARY_SPREADSHEET_COLUMNS;

const { success: successClassName } = FUND_INVESTMENTS_SUMMARY_SPREADSHEET_MARKET_ADJUSTMENT_CLASSNAMES;

const cellParser = (params: CellParserParams): ICell<FundInvestmentSummaryCellData> => {
  const { allRowsExpression, alphabet, colNumber, column, columnLegend, forceReadOnly, key, row, rowNumber } = params;

  const { alias } = getObjectValue(row);
  const { value: cellValue = '' } = getObjectValue(column[alias]);

  // Creating cell from row and column
  const cell: ICell<FundInvestmentSummaryCellData> = { ...row, key, value: cellValue };

  const currentColumn = getFundInvestmentSummaryColumn(colNumber);
  const { tooltip, title } = getObjectValue(currentColumn);

  // Net Capital * (1 + Market Adjustment %)
  const netCapitalColumnLegend = alphabet[NET_CAPITAL.column - 1];
  const marketAdjustmentColumnLegend = alphabet[MARKET_ADJUSTMENT.column - 1];

  const netCapitalColumnExpression = `${netCapitalColumnLegend}${rowNumber}`;
  const marketAdjustmentColumnExpression = `${marketAdjustmentColumnLegend}${rowNumber}`;

  const concludedFairValueOfNetCapitalExpression = `=${netCapitalColumnExpression} * (1 + ${marketAdjustmentColumnExpression})`;

  // Generate the cell based on the alias
  switch (alias) {
    // Generate title cells
    case FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TITLE_ALIAS:
      if (tooltip) {
        cell.tooltipMessages?.push(tooltip);
      }

      cell.value = title;
      break;

    // Generate subheader cells
    case FUND_INVESTMENTS_SUMMARY_SPREADSHEET_INVESTMENT_VEHICLE_ALIAS:
      cell.hidden = true;
      cell.value = '';
      break;

    // Generate total cells
    case FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TOTAL_ALIAS:
      switch (colNumber) {
        case COMMITTED_CAPITAL.column:
        case INVESTED_CAPITAL.column:
        case REALIZED_INCOME.column:
        case UNREALIZED_INCOME.column:
        case DISTRIBUTIONS.column:
        case NET_CAPITAL.column:
        case CONCLUDED_FAIR_VALUE_OF_NET_CAPITAL.column:
          cell.expr = `=SUM(${allRowsExpression})`;
          cell.format = largeCurrencyFormat;
          cell.gridType = 'number';
          break;

        default:
          break;
      }
      break;

    default:
      // Generate column cells
      switch (colNumber) {
        case COMMITTED_CAPITAL.column:
          normalizeFundCell(cell);

          cell.format = largeCurrencyFormat;
          cell.isRequired = true;
          break;

        case INVESTED_CAPITAL.column:
        case REALIZED_INCOME.column:
        case UNREALIZED_INCOME.column:
        case DISTRIBUTIONS.column:
        case NET_CAPITAL.column:
          normalizeFundCell(cell);

          cell.format = largeCurrencyFormat;
          break;

        case MARKET_ADJUSTMENT.column:
          normalizeFundCell(cell);

          cell.className = `${cell.className} ${successClassName}`;
          cell.dbType = 'string';
          cell.format = twoDecimalPercentFormat({ validateFloat: true });
          cell.gridType = 'percentage';
          cell.hidden = isNil(cell?.value);
          cell.readOnly = true;
          break;

        case CONCLUDED_FAIR_VALUE_OF_NET_CAPITAL.column:
          normalizeFundCell(cell);

          cell.expr = concludedFairValueOfNetCapitalExpression;
          cell.format = largeCurrencyFormat;
          cell.readOnly = true;
          break;

        case CAPITAL_ACCOUNT_DATE.column:
          normalizeFundCell(cell);

          cell.dataEditor = (props: DataEditorProps) => <GridDateEditor {...props} />;
          cell.dbType = 'dbDate';
          cell.gridDateComponent = GridDate as FC<GridDateProps>;
          cell.gridType = 'gridDate';
          cell.isRequired = true;
          cell.value = shortDate(toString(cell.value)); // Ensure the date is formatted correctly (e.g., 'yyyy-MM-dd')
          cell.valueViewer = (props: ValueViewerProps) => (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore: GridDateValueViewer is not typed
            <GridDateValueViewer {...props} slashFormatted />
          );
          break;

        default:
          break;
      }
      break;
  }

  // Default values
  const {
    allowEmptyValues = true,
    allowNegativeValue = FUND_INVESTMENTS_SUMMARY_SPREADSHEET_ALLOW_NEGATIVE_VALUE,
    customKey = null,
    dbType = 'string',
    expr = '',
    format = null,
    gridType = 'string',
    maxNumberChars = MAX_CHAR_LENGTH,
    readOnly = true,
    rowSpan = FUND_INVESTMENTS_SUMMARY_SPREADSHEET_ROW_SPAN,
    tooltipMessages = [],
    value = '',
  } = getObjectValue(cell);

  return {
    ...cell,
    allowEmptyValues,
    allowNegativeValue,
    columnLegend,
    customKey,
    dbType,
    expr: getExpression({ expr, columnLegend }),
    format,
    gridType,
    key,
    maxNumberChars,
    readOnly: forceReadOnly || readOnly,
    rowSpan,
    tooltipMessages,
    value,
  } as ICell<FundInvestmentSummaryCellData>;
};

const createParser = (params: CreateParserParams): Cells => {
  const { columns, forceReadOnly, rowConfig } = params;

  const cells = {} as Cells<FundInvestmentSummaryCellData>;

  const alphabet = alphabetGenerator([], columns.length);

  const allRowsExpression = generateAllRowsExpression({
    excludedRows: FUND_INVESTMENTS_SUMMARY_SPREADSHEET_EXCLUDED_ROWS_COUNT,
    excludeLastRow: true,
    rowConfig,
  });

  rowConfig.forEach((row, rowIndex: number) => {
    columns.forEach((column, colIndex: number) => {
      const rowNumber = rowIndex + 1;
      const colNumber = colIndex + 1;
      const columnLegend = alphabet[colIndex];

      const key = columnLegend + rowNumber;

      cells[key] = cellParser({
        allRowsExpression,
        alphabet,
        colNumber,
        column,
        columnLegend,
        forceReadOnly,
        key,
        row,
        rowNumber,
      });
    });
  });

  return cells;
};

export default createParser;
